import { useQuery } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { LIST_LIMIT_MAX } from '../../../core/constants/network';
import { normalizeList, toId, toText } from '../../../common/package/list';

const CATALOG_STALE_MS = 10 * 60_000;

const catalogPaths = {
    projects: () => 'admin/projects',
    repositories: () => 'admin/repositories',
    tiers: () => 'admin/tiers',
};

function lookup(items) {
    return new Map(items.map((item) => [item.id, item]));
}

function useCatalog(key, path, normalize) {
    const params = { limit: LIST_LIMIT_MAX };
    return useQuery({
        queryKey: ['catalogs', key, params],
        queryFn: async ({ signal }) => lookup(normalizeList((await api.get(path, { params, signal })).data, normalize).items),
        staleTime: CATALOG_STALE_MS,
    });
}

// Name lookups shared by home, work, reviews and teams. A missing catalog
// degrades to "Proyecto 4" style labels instead of blocking the screen.
export function useProjectCatalog() {
    return useCatalog('projects', catalogPaths.projects(), (raw) => ({
        id: toId(raw.id), name: toText(raw.name), state: raw.project_state ?? null,
    }));
}

export function useRepositoryCatalog() {
    return useCatalog('repositories', catalogPaths.repositories(), (raw) => ({
        id: toId(raw.id), name: toText(raw.name), kind: raw.kind ?? null, projectId: toId(raw.project_id),
    }));
}

export function useTierCatalog() {
    return useCatalog('tiers', catalogPaths.tiers(), (raw) => ({
        id: toId(raw.id), name: toText(raw.name), projectId: toId(raw.project_id), phase: raw.phase ?? null,
    }));
}

export function nameOf(catalog, id, fallbackPrefix) {
    if (id == null) return '—';
    return catalog?.get(id)?.name || `${fallbackPrefix} ${id}`;
}
